import React, { useState, useEffect } from 'react';
import {
  Search, Filter, Plus, Pencil, UserPlus, UserMinus, Trash2, LogIn, LogOut, Clock, Activity,
  Laptop, Smartphone, Printer, User, Tablet,
} from 'lucide-react';
import Modal from './common/Modal';
import axiosInstance from '../utils/axiosConfig';

/* ─── Mapa de tipo de acción ────────────────────────────────────────────── */
const ACTION_CONFIG = {
  CREAR:      { label: 'Creación',       icon: Plus,      bg: 'bg-green-100',  text: 'text-green-700' },
  INGRESO:    { label: 'Ingreso',        icon: LogIn,     bg: 'bg-green-100',  text: 'text-green-700' },
  ASIGNAR:    { label: 'Asignación',     icon: UserPlus,  bg: 'bg-blue-100',   text: 'text-blue-700' },
  DESASIGNAR: { label: 'Desasignación',  icon: UserMinus, bg: 'bg-amber-100',  text: 'text-amber-700' },
  EDITAR:     { label: 'Modificación',   icon: Pencil,    bg: 'bg-slate-100',  text: 'text-slate-600' },
  ELIMINAR:   { label: 'Eliminación',    icon: Trash2,    bg: 'bg-red-100',    text: 'text-red-700' },
  EGRESO:     { label: 'Egreso',         icon: LogOut,    bg: 'bg-red-100',    text: 'text-red-700' },
  OTRO:       { label: 'Otro',           icon: Activity,  bg: 'bg-indigo-100', text: 'text-indigo-700' },
};

const CATEGORIA_ICONS = {
  smartphones: Smartphone,
  assets: Laptop,
  impresoras: Printer,
  empleados: User,
  tablets: Tablet,
};

const CATEGORIA_LABELS = {
  smartphones: 'Smartphones',
  assets: 'Equipos',
  impresoras: 'Impresoras',
  empleados: 'Empleados',
  tablets: 'Tablets',
};

/* ─── Clasificar acción ─────────────────────────────────────────────────── */
function getActionType(accion, categoria) {
  const u = accion.toUpperCase();

  if (u.includes('DESASIGNA') || u.includes('LIBER') || u.includes('DEVOL')) return 'DESASIGNAR';
  if (u.includes('ASIGNA')) return 'ASIGNAR';

  if (categoria === 'empleados') {
    if (u.includes('CREAR') || u.includes('CREÓ') || u.includes('NUEVO') || u.includes('AÑAD') || u.includes('AGREG')) return 'INGRESO';
    if (u.includes('ELIMINA') || u.includes('ELIMINÓ') || u.includes('BORR')) return 'EGRESO';
  } else {
    if (u.includes('CREAR') || u.includes('CREÓ') || u.includes('NUEVO') || u.includes('AÑAD') || u.includes('AGREG')) return 'CREAR';
    if (u.includes('ELIMINA') || u.includes('ELIMINÓ') || u.includes('BORR')) return 'ELIMINAR';
  }

  if (u.includes('EDIT') || u.includes('MODIFIC') || u.includes('ACTUALIZ') || u.includes('CAMBI')) return 'EDITAR';
  if (u.includes('EGRESO') || u.includes('BAJA') || u.includes('SALIDA')) return 'EGRESO';
  if (u.includes('INGRESO') || u.includes('ALTA') || u.includes('ENTRADA')) return 'INGRESO';

  return 'OTRO';
}

/* ─── Componente ────────────────────────────────────────────────────────── */
function TimelineView({ categoria, onClose, itemId = null, itemName = null }) {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    tipos: { CREAR: true, EDITAR: true, ASIGNAR: true, DESASIGNAR: true, ELIMINAR: true, EGRESO: true, INGRESO: true },
    fechaDesde: '',
    fechaHasta: '',
  });

  useEffect(() => {
    (async () => {
      try {
        let url = `/api/logs/${categoria}`;
        if (itemId) url += `/${itemId}`;
        const { data } = await axiosInstance.get(url);
        setLogs(data);
      } catch (err) {
        setError(err.response?.data?.error || err.message || 'Error cargando el historial');
      } finally {
        setLoading(false);
      }
    })();
  }, [categoria, itemId]);

  /* Filtros */
  const filtered = logs.filter((log) => {
    const match = log.descripcion.toLowerCase().includes(searchTerm.toLowerCase()) ||
                  log.accion.toLowerCase().includes(searchTerm.toLowerCase());
    if (!match) return false;

    // Ocultar cambios de flags "Necesita X" — son ruido de creación de ficha
    if (/Campo 'Necesita .+' modificado/.test(log.descripcion)) return false;

    const type = getActionType(log.accion, categoria);
    if (categoria === 'empleados' && (type === 'ASIGNAR' || type === 'DESASIGNAR')) return false;
    if (!filters.tipos[type] && type !== 'OTRO') return false;

    const d = new Date(log.fecha);
    if (filters.fechaDesde && d < new Date(filters.fechaDesde)) return false;
    if (filters.fechaHasta) {
      const h = new Date(filters.fechaHasta);
      h.setHours(23, 59, 59, 999);
      if (d > h) return false;
    }
    return true;
  });

  // Más nuevo arriba siempre
  const display = [...filtered];

  const CatIcon = CATEGORIA_ICONS[categoria] || Clock;
  const headerTitle = itemName ? `Historial de ${itemName}` : `Historial de ${CATEGORIA_LABELS[categoria] || categoria}`;

  const toggleFilter = (t) => setFilters((p) => ({ ...p, tipos: { ...p.tipos, [t]: !p.tipos[t] } }));
  const clearFilters = () => setFilters({ tipos: { CREAR: true, EDITAR: true, ASIGNAR: true, DESASIGNAR: true, ELIMINAR: true, EGRESO: true, INGRESO: true }, fechaDesde: '', fechaHasta: '' });

  /* Stats */
  const stats = {};
  filtered.forEach((l) => {
    const t = getActionType(l.accion, categoria);
    stats[t] = (stats[t] || 0) + 1;
  });

  return (
    <Modal isOpen={true} onClose={onClose} size="lg">
      <Modal.Header onClose={onClose}>
        <span className="flex items-center gap-2">
          <CatIcon size={18} />
          {headerTitle}
        </span>
      </Modal.Header>
      <Modal.Body>
        {/* Controles */}
        <div className="flex items-center gap-2 mb-3">
          <div className="flex-1 relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Buscar en el historial..."
              className="w-full py-2 pl-9 pr-3 border border-slate-200 rounded-lg text-sm text-slate-700 outline-none focus:border-blue-400 focus:ring-1 focus:ring-blue-400 bg-white"
            />
          </div>
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`flex items-center gap-1.5 py-2 px-3 rounded-lg border text-sm font-medium transition-colors ${
              showFilters ? 'bg-blue-50 border-blue-300 text-blue-700' : 'bg-white border-slate-200 text-slate-600 hover:bg-slate-50'
            }`}
          >
            <Filter size={13} />
            Filtros
          </button>
        </div>

        {/* Panel de filtros */}
        {showFilters && (
          <div className="mb-3 p-3 bg-slate-50 rounded-lg border border-slate-200 space-y-3">
            <div>
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">Tipo de evento</p>
              <div className="flex flex-wrap gap-1.5">
                {Object.entries(ACTION_CONFIG).filter(([k]) => k !== 'OTRO').map(([key, cfg]) => (
                  <button
                    key={key}
                    onClick={() => toggleFilter(key)}
                    className={`flex items-center gap-1 py-1 px-2.5 rounded-md text-xs font-medium border transition-colors ${
                      filters.tipos[key]
                        ? `${cfg.bg} ${cfg.text} border-transparent`
                        : 'bg-white text-slate-400 border-slate-200 line-through'
                    }`}
                  >
                    {cfg.label}
                  </button>
                ))}
              </div>
            </div>
            <div className="flex gap-3 items-end">
              <div className="flex-1">
                <label className="text-xs text-slate-500 mb-1 block">Desde</label>
                <input type="date" value={filters.fechaDesde} onChange={(e) => setFilters((p) => ({ ...p, fechaDesde: e.target.value }))}
                  className="w-full py-1.5 px-2.5 border border-slate-200 rounded-lg text-sm outline-none focus:border-blue-400" />
              </div>
              <div className="flex-1">
                <label className="text-xs text-slate-500 mb-1 block">Hasta</label>
                <input type="date" value={filters.fechaHasta} onChange={(e) => setFilters((p) => ({ ...p, fechaHasta: e.target.value }))}
                  className="w-full py-1.5 px-2.5 border border-slate-200 rounded-lg text-sm outline-none focus:border-blue-400" />
              </div>
              <button onClick={clearFilters} className="py-1.5 px-3 text-xs text-slate-500 hover:text-slate-700 border border-slate-200 rounded-lg hover:bg-slate-50">
                Limpiar
              </button>
            </div>
          </div>
        )}

        {/* Timeline */}
        <div style={{ maxHeight: 'calc(65vh - 120px)', overflowY: 'auto', padding: '4px 0' }}>
          {loading && (
            <div className="space-y-3 skeleton py-6">
              {[1, 2, 3].map((i) => (
                <div key={i} className="flex gap-3">
                  <div className="w-[30px] h-[30px] rounded-full bg-slate-200 flex-shrink-0" />
                  <div className="flex-1 space-y-1.5">
                    <div className="h-3 bg-slate-200 rounded w-3/4" />
                    <div className="h-2.5 bg-slate-100 rounded w-1/2" />
                  </div>
                </div>
              ))}
            </div>
          )}

          {!loading && error && (
            <div className="text-center py-8 text-sm text-red-600 bg-red-50 rounded-lg border border-red-200">
              {error}
            </div>
          )}

          {!loading && !error && filtered.length === 0 && (
            <div className="text-center py-8 text-sm text-slate-400">
              <Clock size={32} className="mx-auto mb-2 opacity-30" />
              {searchTerm || filters.fechaDesde || filters.fechaHasta
                ? 'No se encontraron registros con esos filtros.'
                : 'No hay registros para mostrar.'}
            </div>
          )}

          {!loading && !error && display.length > 0 && (
            <div className="relative">
              {/* Línea vertical */}
              <div className="absolute left-[15px] top-2 bottom-2 w-0.5 bg-slate-200" />

              <div className="space-y-0">
                {display.map((log, idx) => {
                  const type = getActionType(log.accion, categoria);
                  const cfg = ACTION_CONFIG[type] || ACTION_CONFIG.OTRO;
                  const ActionIcon = cfg.icon;
                  const fecha = new Date(log.fecha);
                  const isFirst = idx === 0;

                  return (
                    <div key={log.id} className="relative flex gap-3 pb-4 last:pb-0">
                      {/* Nodo */}
                      <div className={`relative z-10 flex-shrink-0 w-[30px] h-[30px] rounded-full flex items-center justify-center ${cfg.bg} ${cfg.text}`}>
                        <ActionIcon size={13} />
                      </div>

                      {/* Card */}
                      <div className={`flex-1 rounded-lg border px-3 py-2.5 min-w-0 ${
                        isFirst ? 'border-blue-200 bg-blue-50/40' : 'border-slate-100 bg-slate-50/50'
                      }`}>
                        <div className="flex items-start justify-between gap-2">
                          <span className={`text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded ${cfg.bg} ${cfg.text}`}>
                            {cfg.label}
                          </span>
                          <span className="text-[10px] text-slate-400 whitespace-nowrap mt-0.5">
                            {fecha.toLocaleDateString('es-ES', { day: '2-digit', month: 'short', year: 'numeric' })}
                            {' '}
                            {fecha.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' })}
                          </span>
                        </div>
                        <p className="text-sm text-slate-700 mt-1.5 leading-relaxed break-words">
                          {log.descripcion}
                        </p>
                        {log.usuario && (
                          <p className="text-[11px] text-slate-400 mt-1">por {log.usuario}</p>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Resumen */}
              <div className="mt-4 pt-3 border-t border-slate-100 flex flex-wrap items-center gap-3 text-xs text-slate-400">
                <span>{filtered.length} registro{filtered.length !== 1 ? 's' : ''}</span>
                {Object.entries(stats).map(([type, count]) => {
                  const cfg = ACTION_CONFIG[type];
                  if (!cfg) return null;
                  return (
                    <span key={type} className={`px-1.5 py-0.5 rounded ${cfg.bg} ${cfg.text}`}>
                      {count} {cfg.label.toLowerCase()}
                    </span>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </Modal.Body>
      <Modal.Footer>
        <button className="btn-primary" onClick={onClose}>Cerrar</button>
      </Modal.Footer>
    </Modal>
  );
}

export default TimelineView;
